import { Instance, SnapshotIn, types, flow, getEnv } from 'mobx-state-tree';
import { DataState, DataStateStore } from '@ace/core';

import { IStoresEnv } from '@core/storesEnv';
import {
    performApprovePairing,
    performDenyPairing,
    performGetPairableUsers,
    performGetPairedDevices,
    performLookupPairingCode,
    performRevokePairedDevice,
    PairableUser,
    PairedDevice,
} from 'AuthPart/DevicePairing/services';

const PAIRABLE_USERS_PAGE_SIZE = 25;

export const PENDING_DEVICE_OUTCOME = '';
export const APPROVED_DEVICE_OUTCOME = 'approved';
export const DENIED_DEVICE_OUTCOME = 'denied';

// No I/1 and no O/0: this is read off a headset screen across a room and typed by hand.
export const PAIRING_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const PAIRING_CODE_LENGTH = 6;

/**
 * Whitespace and hyphens are how people write a code down, case is how they type it.
 * The server normalizes the same way, so what is displayed and what is checked stay identical.
 */
export const normalizePairingCode = (code: string): string =>
    (code || '').replace(/[\s\-_]/g, '').toUpperCase();

export const isCompletePairingCode = (code: string): boolean => {
    const normalized = normalizePairingCode(code);

    return normalized.length === PAIRING_CODE_LENGTH &&
        normalized.split('').every(c => PAIRING_CODE_ALPHABET.includes(c));
};

const PairingDeviceInfo = types.model('PairingDeviceInfo', {
    deviceName: types.optional(types.string, ''),
    devicePlatform: types.optional(types.string, ''),
    applicationVersion: types.optional(types.string, ''),
    requestIp: types.optional(types.string, ''),
    requestedAt: types.optional(types.string, ''),
    expiresAt: types.optional(types.string, ''),
});

/**
 * The person a device is being paired for, when that is not the signed-in user.
 * Kept as a snapshot of what was picked rather than an id alone, so the confirmation step can
 * name them without another round trip.
 */
const PairingTarget = types.model('PairingTarget', {
    id: types.string,
    fullName: types.optional(types.string, ''),
    email: types.optional(types.string, ''),
});

export const DevicePairingUIStoreInferred = types
    .model('DevicePairingUIStoreInferred', {
        modalOpened: types.optional(types.boolean, false),
        processError: types.optional(types.string, ''),
        code: types.optional(types.string, ''),
        deviceInfo: types.maybeNull(PairingDeviceInfo),
        outcome: types.optional(types.string, PENDING_DEVICE_OUTCOME),
        // Null means "pair this to me", which is what everybody without the privilege gets.
        pairFor: types.maybeNull(PairingTarget),
        // Whether the "someone else" branch is open. Tracked apart from pairFor so that choosing
        // that branch and then picking nobody is a blocked state rather than a silent fallback
        // to pairing the device to yourself.
        pairForOther: types.optional(types.boolean, false),
    })
    .volatile(() => ({
        lookupStatus: DataStateStore.create({ state: DataState.initial }),
        actionStatus: DataStateStore.create({ state: DataState.initial }),
        devicesStatus: DataStateStore.create({ state: DataState.initial }),
        pairableUsersStatus: DataStateStore.create({ state: DataState.initial }),
        devices: [] as PairedDevice[],
        pairableUsers: [] as PairableUser[],
    }))
    .actions(self => {
        const { api } = getEnv<IStoresEnv>(self);

        const readError = (result: any) => {
            const { body } = result.error || {};

            return body?.errors?.[0] || '';
        };

        const reset = () => {
            self.processError = '';
            self.code = '';
            self.deviceInfo = null;
            self.outcome = PENDING_DEVICE_OUTCOME;
            self.pairFor = null;
            self.pairForOther = false;
            self.pairableUsers = [];
            self.lookupStatus.setInitial();
            self.actionStatus.setInitial();
            self.pairableUsersStatus.setInitial();
        };

        const lookup = flow(function* (code: string) {
            const normalized = normalizePairingCode(code);

            self.processError = '';
            self.deviceInfo = null;
            self.outcome = PENDING_DEVICE_OUTCOME;
            self.lookupStatus.setLoading();

            const result = yield performLookupPairingCode(api, {
                payload: { code: normalized },
                errorHandlers: { DEFAULT: '' },
                disableDefaultErrorHandlers: true,
            });

            if (result.success) {
                self.code = normalized;
                // ApiStore hands back the parsed response body directly.
                self.deviceInfo = result.data || null;
                self.lookupStatus.setDone();
            } else {
                self.code = '';
                self.processError = readError(result);
                self.lookupStatus.setError();
            }
        });

        /**
         * Drops a rejected-code error as soon as the user starts correcting it — leaving the boxes
         * red while they retype says the new characters are wrong, which is not known yet.
         */
        const clearLookupError = () => {
            if (self.lookupStatus.isError) {
                self.processError = '';
                self.lookupStatus.setInitial();
            }
        };

        const openModal = (code?: string) => {
            reset();
            self.modalOpened = true;

            if (code && isCompletePairingCode(code)) {
                lookup(code);
            }
        };

        const closeModal = () => {
            self.modalOpened = false;
            reset();
        };

        /**
         * Who this pairing is for. Passing null hands the device back to the signed-in user, which
         * is the state the dialog always starts in.
         */
        const setPairFor = (user: PairableUser | null) => {
            self.pairFor = user
                ? { id: user.id, fullName: user.fullName || user.email, email: user.email || '' }
                : null;
        };

        const choosePairForSelf = () => {
            self.pairForOther = false;
            self.pairFor = null;
        };

        const choosePairForOther = () => {
            self.pairForOther = true;
        };

        const searchPairableUsers = flow(function* (searchText: string) {
            self.pairableUsersStatus.setLoading();

            const result = yield performGetPairableUsers(api, {
                payload: { searchText: searchText || '', limit: PAIRABLE_USERS_PAGE_SIZE, page: 0 },
                errorHandlers: { DEFAULT: '' },
                disableDefaultErrorHandlers: true,
            });

            if (result.success) {
                self.pairableUsers = result.data?.entities || [];
                self.pairableUsersStatus.setDone();
            } else {
                self.pairableUsers = [];
                self.pairableUsersStatus.setError();
            }
        });

        const approve = flow(function* () {
            self.processError = '';
            self.actionStatus.setLoading();

            const result = yield performApprovePairing(api, {
                payload: { code: self.code, userId: self.pairFor?.id },
                errorHandlers: { DEFAULT: '' },
                disableDefaultErrorHandlers: true,
            });

            if (result.success) {
                self.outcome = APPROVED_DEVICE_OUTCOME;
                self.actionStatus.setDone();
            } else {
                self.processError = readError(result);
                self.actionStatus.setError();
            }
        });

        const deny = flow(function* () {
            self.processError = '';
            self.actionStatus.setLoading();

            const result = yield performDenyPairing(api, {
                payload: { code: self.code },
                errorHandlers: { DEFAULT: '' },
                disableDefaultErrorHandlers: true,
            });

            if (result.success) {
                self.outcome = DENIED_DEVICE_OUTCOME;
                self.actionStatus.setDone();
            } else {
                self.processError = readError(result);
                self.actionStatus.setError();
            }
        });

        const getDevices = flow(function* () {
            self.devicesStatus.setLoading();

            const result = yield performGetPairedDevices(api, {
                payload: {},
                errorHandlers: { DEFAULT: '' },
                disableDefaultErrorHandlers: true,
            });

            if (result.success) {
                self.devices = result.data || [];
                self.devicesStatus.setDone();
            } else {
                self.devicesStatus.setError();
            }
        });

        const revokeDevice = flow(function* (id: string) {
            self.devicesStatus.setLoading();

            const result = yield performRevokePairedDevice(api, {
                payload: { id },
                errorHandlers: { DEFAULT: '' },
                disableDefaultErrorHandlers: true,
            });

            if (result.success) {
                self.devices = self.devices.filter(device => device.id !== id);
                self.devicesStatus.setDone();
            } else {
                self.devicesStatus.setError();
            }

            return result;
        });

        return ({
            openModal,
            closeModal,
            clearLookupError,
            lookup,
            approve,
            deny,
            getDevices,
            revokeDevice,
            setPairFor,
            choosePairForSelf,
            choosePairForOther,
            searchPairableUsers,
            reset,
        });
    })
    .views(self => ({
        /**
         * True when approving would sign the device in as somebody other than the person doing it.
         * Drives the wording on the confirmation step, which otherwise says "as you".
         */
        get isPairingOnBehalf() {
            return !!self.pairFor;
        },

        /**
         * "Someone else" is chosen but nobody has been picked. Approving here would quietly pair
         * the device to the person at the keyboard, which is the opposite of what was asked for.
         */
        get isPairTargetIncomplete() {
            return self.pairForOther && !self.pairFor;
        },
    }));

type DevicePairingUIStoreFactoryType = typeof DevicePairingUIStoreInferred;
interface IDevicePairingUIStoreFactory extends DevicePairingUIStoreFactoryType { }
export const DevicePairingUIStore: IDevicePairingUIStoreFactory = DevicePairingUIStoreInferred;
export interface IDevicePairingUIStore extends Instance<IDevicePairingUIStoreFactory> { }
export interface IDevicePairingUIStoreSnapshotIn extends SnapshotIn<IDevicePairingUIStore> { }
